/**
 * Policy Drawer Component with Form
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Drawer,
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
  FormControlLabel,
  Switch,
  MenuItem,
  CircularProgress,
  Divider,
  Paper,
  Toolbar,
  Tooltip,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import CloseIcon from '@mui/icons-material/Close';
import GavelIcon from '@mui/icons-material/Gavel';
import SaveIcon from '@mui/icons-material/Save';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import LinkIcon from '@mui/icons-material/Link';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import {
  getBookingDrawerTheme,
  getBookingDrawerPaperSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerFooterSx,
  getBookingDrawerPanelSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPrimaryButtonSx,
  getBookingDrawerAuditButtonSx,
  getBookingDrawerInputBg,
  getBookingDrawerDividerColor,
  BOOKING_DRAWER_ACCENT,
} from '../../utils/bookingDrawerLayout';
import {
  createDrawerOverlayProps,
  getDrawerRootSx,
} from '../../utils/drawerOverlayProps';
import { POLICY_TYPES, THEME_COLOR } from '../../constants/policies';

export const PolicyDrawer = ({
  open,
  policy,
  isDark,
  saving,
  onClose,
  onSave,
}) => {
  const drawerTheme = getBookingDrawerTheme(Boolean(isDark));
  const drawerOverlays = useMemo(
    () => createDrawerOverlayProps(Boolean(isDark), isDark ? drawerTheme.surface : '#ffffff'),
    [isDark, drawerTheme.surface]
  );
  const [formData, setFormData] = useState({
    policy_type: '',
    title: '',
    content: '',
    summary: '',
    version: '',
    effective_date: null,
    expiry_date: null,
    is_active: true,
    requires_acknowledgment: false,
    sort_order: 0,
  });
  const [errors, setErrors] = useState({});
  const editorRef = useRef(null);
  const [editorKey, setEditorKey] = useState(0);
  const contentInitializedRef = useRef(false);

  useEffect(() => {
    if (open) {
      contentInitializedRef.current = false; // Reset flag when drawer opens
      
      if (policy) {
        setFormData({
          policy_type: policy.policy_type || '',
          title: policy.title || '',
          content: policy.content || '',
          summary: policy.summary || '',
          version: policy.version || '',
          effective_date: policy.effective_date ? moment(policy.effective_date) : null,
          expiry_date: policy.expiry_date ? moment(policy.expiry_date) : null,
          is_active: policy.is_active !== undefined ? policy.is_active : true,
          requires_acknowledgment: policy.requires_acknowledgment || false,
          sort_order: policy.sort_order || 0,
        });
      } else {
        setFormData({
          policy_type: '',
          title: '',
          content: '',
          summary: '',
          version: '',
          effective_date: null,
          expiry_date: null,
          is_active: true,
          requires_acknowledgment: false,
          sort_order: 0,
        });
      }
      setErrors({});
      // Force editor to re-render when drawer opens
      setEditorKey(prev => prev + 1);
    } else {
      contentInitializedRef.current = false;
    }
  }, [policy, open]);

  // Set editor content only once when drawer opens and editor is ready
  useEffect(() => {
    if (open && editorRef.current && !contentInitializedRef.current) {
      const timer = setTimeout(() => {
        if (editorRef.current && !contentInitializedRef.current) {
          if (policy && policy.content) {
            editorRef.current.innerHTML = policy.content;
          } else {
            editorRef.current.innerHTML = '<p></p>';
          }
          contentInitializedRef.current = true; // Mark as initialized
        }
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [open, policy, editorKey]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleDateChange = (field) => (newValue) => {
    setFormData(prev => ({ ...prev, [field]: newValue }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleContentChange = () => {
    if (editorRef.current) {
      setFormData(prev => ({ ...prev, content: editorRef.current.innerHTML }));
    }
  };

  const formatText = (command, value = null) => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand(command, false, value);
      handleContentChange();
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.policy_type) {
      newErrors.policy_type = 'Policy type is required';
    }
    if (!formData.title?.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.content?.trim()) {
      newErrors.content = 'Content is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validate()) {
      // Convert moment dates to ISO strings for API
      const saveData = {
        ...formData,
        effective_date: formData.effective_date ? formData.effective_date.format('YYYY-MM-DD') : '',
        expiry_date: formData.expiry_date ? formData.expiry_date.format('YYYY-MM-DD') : '',
      };
      onSave(saveData);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={getDrawerRootSx()}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 600 },
          backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
        {/* Header — matches Room Type drawer */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.5,
            borderBottom: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
            backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GavelIcon sx={{ color: THEME_COLOR, fontSize: 24 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: isDark ? drawerTheme.text : '#333' }}>
              {policy ? 'Edit Policy' : 'Add New Policy'}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
            onClick={onClose}
            disabled={saving}
            sx={{
              borderColor: isDark ? 'rgba(76, 225, 182, 0.35)' : 'rgba(76, 225, 182, 0.45)',
              color: THEME_COLOR,
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '999px',
              px: 1.5,
              py: 0.3,
              minWidth: 84,
              '&:hover': {
                borderColor: THEME_COLOR,
                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)',
              },
              '&.Mui-disabled': {
                borderColor: isDark ? drawerTheme.surfaceBorder : '#d0d0d0',
                color: isDark ? drawerTheme.subtleText : '#999',
              },
            }}
          >
            Close
          </Button>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Policy Type */}
            <TextField
              select
              label="Policy Type"
              value={formData.policy_type}
              onChange={handleChange('policy_type')}
              error={!!errors.policy_type}
              helperText={errors.policy_type}
              fullWidth
              required
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: THEME_COLOR,
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: THEME_COLOR,
                      borderWidth: '2px',
                    },
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: THEME_COLOR,
                },
              }}
              SelectProps={{
                MenuProps: {
                  ...drawerOverlays.selectMenuProps,
                  PaperProps: {
                    ...drawerOverlays.selectMenuProps.PaperProps,
                    sx: {
                      ...drawerOverlays.selectMenuProps.PaperProps?.sx,
                      maxHeight: 300,
                      backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                    },
                  },
                },
              }}
            >
              {POLICY_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>

            {/* Title */}
            <TextField
              label="Title"
              value={formData.title}
              onChange={handleChange('title')}
              error={!!errors.title}
              helperText={errors.title}
              fullWidth
              required
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: THEME_COLOR,
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: THEME_COLOR,
                      borderWidth: '2px',
                    },
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: THEME_COLOR,
                },
              }}
            />

            {/* Summary */}
            <TextField
              label="Summary"
              value={formData.summary}
              onChange={handleChange('summary')}
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              helperText="Brief summary or key points of the policy"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: THEME_COLOR,
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: THEME_COLOR,
                      borderWidth: '2px',
                    },
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: THEME_COLOR,
                },
              }}
            />

            {/* Version */}
            <TextField
              label="Version"
              value={formData.version}
              onChange={handleChange('version')}
              fullWidth
              variant="outlined"
              helperText="Leave empty to auto-generate version number"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: THEME_COLOR,
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: THEME_COLOR,
                      borderWidth: '2px',
                    },
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: THEME_COLOR,
                },
              }}
            />

            {/* Dates Row */}
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <DatePicker
                  label="Effective Date"
                  value={formData.effective_date}
                  onChange={handleDateChange('effective_date')}
                  slotProps={{
                    ...drawerOverlays.datePickerSlotProps,
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      error: !!errors.effective_date,
                      helperText: errors.effective_date,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                          '&:hover': {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: THEME_COLOR,
                            },
                          },
                          '&.Mui-focused': {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: THEME_COLOR,
                              borderWidth: '2px',
                            },
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: THEME_COLOR,
                        },
                      },
                    },
                  }}
                />
                <DatePicker
                  label="Expiry Date"
                  value={formData.expiry_date}
                  onChange={handleDateChange('expiry_date')}
                  slotProps={{
                    ...drawerOverlays.datePickerSlotProps,
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      error: !!errors.expiry_date,
                      helperText: errors.expiry_date,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                          '&:hover': {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: THEME_COLOR,
                            },
                          },
                          '&.Mui-focused': {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: THEME_COLOR,
                              borderWidth: '2px',
                            },
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: THEME_COLOR,
                        },
                      },
                    },
                  }}
                />
              </Box>
            </LocalizationProvider>

            {/* Content Editor */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Content <span style={{ color: 'red' }}>*</span>
              </Typography>
              
              {/* Editor Toolbar */}
              <Paper
                variant="outlined"
                square
                sx={{
                  borderBottom: 'none',
                  borderTopLeftRadius: 1,
                  borderTopRightRadius: 1,
                }}
              >
                <Toolbar variant="dense" sx={{ minHeight: 48, gap: 0.5, flexWrap: 'wrap' }}>
                  <Tooltip title="Bold">
                    <IconButton size="small" onClick={() => formatText('bold')}>
                      <FormatBoldIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Italic">
                    <IconButton size="small" onClick={() => formatText('italic')}>
                      <FormatItalicIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                  <Tooltip title="Bullet List">
                    <IconButton size="small" onClick={() => formatText('insertUnorderedList')}>
                      <FormatListBulletedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Numbered List">
                    <IconButton size="small" onClick={() => formatText('insertOrderedList')}>
                      <FormatListNumberedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                  <Tooltip title="Align Left">
                    <IconButton size="small" onClick={() => formatText('justifyLeft')}>
                      <FormatAlignLeftIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Align Center">
                    <IconButton size="small" onClick={() => formatText('justifyCenter')}>
                      <FormatAlignCenterIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Align Right">
                    <IconButton size="small" onClick={() => formatText('justifyRight')}>
                      <FormatAlignRightIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Justify">
                    <IconButton size="small" onClick={() => formatText('justifyFull')}>
                      <FormatAlignJustifyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                  <Tooltip title="Insert Link">
                    <IconButton size="small" onClick={() => formatText('createLink')}>
                      <LinkIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Quote">
                    <IconButton size="small" onClick={() => formatText('formatBlock', 'blockquote')}>
                      <FormatQuoteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Toolbar>
              </Paper>

              {/* Editor */}
              <Box
                key={editorKey}
                component="div"
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={handleContentChange}
                onBlur={handleContentChange}
                sx={{
                  minHeight: 300,
                  p: 2,
                  border: 1,
                  borderColor: errors.content ? 'error.main' : 'divider',
                  borderTop: 'none',
                  borderBottomLeftRadius: 1,
                  borderBottomRightRadius: 1,
                  bgcolor: 'background.paper',
                  color: 'text.primary',
                  fontFamily: 'inherit',
                  fontSize: '0.95rem',
                  lineHeight: 1.6,
                  outline: 'none',
                  '&:focus': {
                    borderColor: errors.content ? 'error.main' : THEME_COLOR,
                    borderWidth: 2,
                  },
                  '& p': { margin: '0.5em 0' },
                  '& ul, & ol': { margin: '0.5em 0', paddingLeft: '2em' },
                  '& strong': { fontWeight: 600 },
                  '& em': { fontStyle: 'italic' },
                  '& blockquote': {
                    borderLeft: 4,
                    borderColor: THEME_COLOR,
                    paddingLeft: 2,
                    margin: '1em 0',
                    fontStyle: 'italic',
                    color: 'text.secondary',
                  },
                }}
              />
              {errors.content && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {errors.content}
                </Typography>
              )}
            </Box>

            {/* Switches Row */}
            <Box sx={{ display: 'flex', gap: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={handleChange('is_active')}
                    color="primary"
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.requires_acknowledgment}
                    onChange={handleChange('requires_acknowledgment')}
                    color="primary"
                  />
                }
                label="Requires Acknowledgment"
              />
            </Box>

            {/* Sort Order */}
            <TextField
              label="Sort Order"
              type="number"
              value={formData.sort_order}
              onChange={handleChange('sort_order')}
              fullWidth
              variant="outlined"
              helperText="Display order for sorting policies"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: THEME_COLOR,
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: THEME_COLOR,
                      borderWidth: '2px',
                    },
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: THEME_COLOR,
                },
              }}
            />
          </Box>
        </Box>

        {/* Footer — matches Room Type drawer */}
        <Box
          sx={{
            p: 1.5,
            borderTop: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
            backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
          }}
        >
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <Button
                variant="outlined"
                fullWidth
                onClick={onClose}
                disabled={saving}
                sx={{
                  borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                  color: isDark ? drawerTheme.text : '#666',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: THEME_COLOR,
                    backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                    color: THEME_COLOR,
                  },
                  '&:disabled': {
                    borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                    color: isDark ? drawerTheme.subtleText : '#999',
                  },
                }}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                variant="contained"
                fullWidth
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{
                  backgroundColor: THEME_COLOR,
                  color: '#000',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: THEME_COLOR,
                    opacity: 0.9,
                    boxShadow: `0 4px 12px ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                  },
                  '&:disabled': {
                    backgroundColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                    color: isDark ? drawerTheme.subtleText : '#666',
                  },
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Drawer>
  );
};

